// Libraries
import { Fragment, useCallback, useMemo } from 'react';
import { isToday } from 'date-fns';
// Components
import { Pill } from '../index';
import { Tables, useDatePickerStore, useUnitAndRangeStore } from '@stores/index';
// Utils
import text from '@translations/en.json';
// Types
import { getShortNumber } from '@helpers/index';
import { FILTER_GROUP_LABEL, PillGroupProps } from './PillGroup.types';

/**
 * Renders a group of pills based on the provided filter group and filters.
 *
 * @component
 * @param {PillGroupProps} PillGroupProps - The props for the PillGroup component.
 * @param {ColumnFilter} filterGroup - The filter group object containing information about the group.
 * @param {setColumnFilters} setColumnFilters - A function to set column filters.
 * @param {ColumnFiltersStatePills[]} columnFilters - An array of column filters.
 * @returns {ReactElement} A fragment containing the rendered pills.
 */
export const PillGroup = ({ filterGroup, setColumnFilters }: PillGroupProps) => {
  const {
    [Tables.CII_FLEET_RATINGS]: { selectedDate },
  } = useDatePickerStore();

  const dateSelectedIsToday = isToday(selectedDate.to[0]);

  const { minValue, maxValue, hasRangePill } = useUnitAndRangeStore();

  const filterValue = useMemo(() => {
    if (filterGroup.id === 'capacityAndUnit') {
      const unitValue = ((filterGroup.value as string[]) ?? []).reduce((acc, curr) => {
        const [, unit] = curr.split('/');
        if (!acc[0]) {
          acc[0] = unit;
        }

        if (hasRangePill) {
          acc[1] = `${text.filter_capacity} ${getShortNumber(minValue)} - ${getShortNumber(maxValue)} `;
        }
        return acc;
      }, [] as string[]);
      return unitValue;
    }
    return filterGroup.value as string[];
    // Note: We don't need to track the changes on the unit and range values because they are going to be updated at this point
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGroup]);

  const periodCIIPillLabel = dateSelectedIsToday ? FILTER_GROUP_LABEL.CURRENT_RATING : FILTER_GROUP_LABEL.PERIOD_CII;

  const label = useCallback(
    (value: string) => {
      switch (filterGroup.id) {
        case 'forecastedCiiRating':
          return `${FILTER_GROUP_LABEL.FORECASTED_RATING} ${value}`;
        case 'periodCii':
          return `${periodCIIPillLabel} ${value}`;
        case 'endOfPeriod':
          return `${FILTER_GROUP_LABEL.END_OF_PERIOD} ${value}`;
        case 'startOfPeriod':
          return `${FILTER_GROUP_LABEL.START_OF_PERIOD} ${value}`;
        case 'capacityAndUnit':
        default:
          return value;
      }
    },
    [filterGroup.id, periodCIIPillLabel]
  );

  return (
    <Fragment>
      {filterValue.map((filter) => (
        <Pill
          filter={filter}
          filterGroupId={filterGroup.id}
          key={`${filterGroup.id}-${filter}`}
          setColumnFilters={setColumnFilters}
          label={label(filter)}
        />
      ))}
    </Fragment>
  );
};
